<template>
  <geo-json-source
    ref="source"
    :cluster="true"
    :cluster-max-zoom="14"
    :cluster-radius="50"
    :cluster-properties="clusterProperties"
    :data="geoJsonData || { type: 'FeatureCollection', features: [] }"
    :source-id="sourceId"
  >
    <layer-group
      :hide-layer="hideLayer || !showLayers"
      :layers="layers"
      :filter="filters"
      :before="props.before"
      @click="handleClick"
    />
  </geo-json-source>
</template>

<script setup lang="ts">
import { GeoJSONFeature, MapMouseEvent, SymbolLayerSpecification } from 'mapbox-gl'
import { computed } from 'vue'
import GeoJsonSource from '../mapbox/GeoJsonSource.vue'
import LayerGroup from '../mapbox/LayerGroup.vue'
import { useJobsGeoJSON } from '../composables/useJobsGeoJSON'

const props = defineProps<{
  layerId: string
  sourceId: string
  before?: string
  hideLayer?: boolean
  hiddenLocationIds?: (string | number)[]
}>()

const emits = defineEmits<{
  (event: 'click', data: GeoJSONFeature[], eventData: MapMouseEvent)
}>()

// Use the extracted composable
const { geoJsonData, isComponentMounted, error } = useJobsGeoJSON()

// Only show layers when we have data, component is mounted, and not in error state
const showLayers = computed(() => {
  return (
    isComponentMounted.value &&
    !error.value &&
    geoJsonData.value &&
    geoJsonData.value.features &&
    geoJsonData.value.features.length > 0
  )
})

const handleClick = (data: GeoJSONFeature[], eventData: MapMouseEvent) => {
  emits('click', data, eventData)
}

const clusterProperties = {
  // Sum all TotalCount values in the cluster
  TotalCount: ['+', ['get', 'TotalCount']],
  // Sum pickup totals
  PickupTotal: ['+', ['get', 'Total', ['get', 'Pickup']]],
  // Sum dropoff totals
  DropoffTotal: ['+', ['get', 'Total', ['get', 'Dropoff']]],
}

const layers = computed<SymbolLayerSpecification[]>(() => {
  return [
    {
      id: props.layerId,
      type: 'symbol',
      source: props.sourceId,
      filter: ['!', ['has', 'point_count']], // Only show individual points
      layout: {
        'icon-allow-overlap': true,
        'icon-image': ['concat', 'job-cluster', '__', '#eeeeee', '__', ['get', 'TotalCount']],
        // LocationName can contain Emojis, and Mapbox does not support them, so you may see "glyphs > 65535 not supported" warning in the console
        // https://github.com/mapbox/mapbox-gl-js/issues/13500
        'text-field': ['get', 'LocationName'],
        'text-font': ['Montserrat Medium', 'Arial Unicode MS Regular'],
        'text-radial-offset': 2,
        'text-line-height': 1.1,
        'text-allow-overlap': false,
        'text-optional': true,
        'text-anchor': 'top',
        'text-letter-spacing': 0.1,
        'text-size': {
          stops: [
            [14, 0],
            [15, 11],
            [15.5, 12],
            [17, 14],
          ],
        },
      },
      paint: {
        'text-halo-color': 'hsl(0, 5%, 0%)',
        'text-color': 'hsl(0, 0%, 100%)',
        'text-halo-width': 2,
        'text-halo-blur': 0.5,
        'text-opacity': {
          stops: [
            [15.5, 0],
            [16, 1],
          ],
        },
      },
    },
    {
      id: 'cluster-count',
      type: 'symbol',
      source: props.sourceId,
      filter: ['has', 'point_count'], // Only show clusters
      layout: {
        'text-field': ['get', 'TotalCount'],
        'text-optional': false,
        'text-allow-overlap': true,
        'text-font': ['Montserrat Bold', 'Arial Unicode MS Regular'],
        'text-size': 12,
      },
    },
  ] as SymbolLayerSpecification[]
})

const filters = computed(() => {
  const filter: any[] = ['all']

  const ids = props.hiddenLocationIds || []
  if (ids.length > 0) {
    filter.push(['!', ['in', ['get', 'LocationId'], ['literal', ids]]])
  }

  return filter
})
</script>
